import type { TextStyleOptions } from "pixi.js";
import type { LocalizationService } from "./LocalizationService";
import { FittedText } from "@slot/presentation";
import type { TextFitBox } from "@slot/presentation";

/** Params interpolated into the localized string (`{count}` style). */
export type LocalizedTextParams = Record<string, string | number>;

/**
 * A `FittedText` bound to a localization key. Re-resolves
 * on `refresh()` (locale change) and on `setParams()`. Explicit `text` bypasses the key.
 */
export class LocalizedText extends FittedText {
  private readonly localization: LocalizationService;
  private key: string | null;
  private fallback: string;
  private params: LocalizedTextParams | undefined;

  public constructor(input: {
    localization: LocalizationService;
    key?: string | null;
    fallback?: string;
    params?: LocalizedTextParams;
    style: TextStyleOptions;
    anchor?: number | { x: number; y: number };
    label?: string;
    /** `overflow: SHRINK` box: the text scales down (never up) to fit it, on every refresh. */
    fitBox?: TextFitBox;
  }) {
    super({ text: "", style: input.style, label: input.label ?? `Text:${input.key ?? "plain"}`, fitBox: input.fitBox ?? null });
    this.localization = input.localization;
    this.key = input.key ?? null;
    this.fallback = input.fallback ?? "";
    this.params = input.params;
    if (input.anchor !== undefined) {
      if (typeof input.anchor === "number") this.anchor.set(input.anchor);
      else this.anchor.set(input.anchor.x, input.anchor.y);
    } else {
      this.anchor.set(0.5);
    }
    this.refresh();
  }

  public setKey(key: string, fallback?: string, params?: LocalizedTextParams): void {
    this.key = key;
    if (fallback !== undefined) this.fallback = fallback;
    this.params = params;
    this.refresh();
  }

  public setParams(params: LocalizedTextParams): void {
    this.params = params;
    this.refresh();
  }

  /** Re-resolve the key (locale change); the write re-fits. No-op for plain text. */
  public refresh(): void {
    if (this.key === null) return;
    this.text = this.localization.t(this.key, this.fallback, this.params);
  }
}
